/**
 * Подготовка URL редиректа после survey: только значение query `redirect_uri`, allowlist по origin, fallback и cmpimport.
 *
 * @module utils/surveyRedirectTarget
 */

import { applyCmpimportToRedirectUrl } from './cmpimportHash';
import { getParamFromURL } from './paramsUrl';
import { isRedirectOriginAllowed, parseSurveyRedirectAllowlistRaw } from './surveyRedirectAllowlist';

const LITE_ADMITAD_HOSTNAME = 'lite.admitad.com';

function isLiteAdmitadRedirectUri(redirectUri: string): boolean {
  let target: URL;
  try {
    target = new URL(redirectUri);
  } catch {
    return false;
  }

  if (target.protocol !== 'http:' && target.protocol !== 'https:') {
    return false;
  }

  if (target.hostname.toLowerCase() !== LITE_ADMITAD_HOSTNAME) {
    return false;
  }

  if (target.protocol === 'https:') {
    return target.port === '' || target.port === '443';
  }
  return target.port === '' || target.port === '80';
}

/**
 * Возвращает обязательный fallback URL из `VITE_DEFAULT_REDIRECT_URL`.
 *
 * @returns {string}
 * @throws {Error} если переменная пуста
 */
export function getSurveyDefaultRedirectUrl(): string {
  const raw = import.meta.env.VITE_DEFAULT_REDIRECT_URL;
  if (typeof raw !== 'string' || raw.trim() === '') {
    throw new Error('VITE_DEFAULT_REDIRECT_URL must be a non-empty string');
  }
  return raw.trim().replace(/\/$/, '');
}

/**
 * Читает значение `redirect_uri` только из query переданного URL (строгий режим).
 *
 * @param {string} pageUrl — полный URL страницы (например `window.location.href`)
 * @returns {string | null} декодированное значение или null
 */
export function readRedirectUriFromSurveyPageUrl(pageUrl: string): string | null {
  return getParamFromURL(pageUrl, 'redirect_uri');
}

/**
 * Итоговый URL выхода по **уже извлечённому** значению `redirect_uri` (не по полному URL страницы survey).
 * Пустое / невалидное / не из allowlist → `VITE_DEFAULT_REDIRECT_URL` с cmpimport.
 *
 * @param {string | null} redirectUri — значение query-параметра `redirect_uri`
 * @returns {string} абсолютный URL с учётом cmpimport
 */
export function resolveExitUrlFromRedirectUriParam(redirectUri: string | null): string {
  const fallback = getSurveyDefaultRedirectUrl();
  const allowlist = parseSurveyRedirectAllowlistRaw();

  if (redirectUri === null || redirectUri === '') {
    return applyCmpimportToRedirectUrl(fallback);
  }

  let target: URL;
  try {
    target = new URL(redirectUri);
  } catch {
    return applyCmpimportToRedirectUrl(fallback);
  }

  if (target.protocol !== 'http:' && target.protocol !== 'https:') {
    return applyCmpimportToRedirectUrl(fallback);
  }

  const origin = target.origin;
  if (!isRedirectOriginAllowed(origin, allowlist)) {
    return applyCmpimportToRedirectUrl(fallback);
  }

  return applyCmpimportToRedirectUrl(redirectUri);
}

/**
 * Редирект на `lite.admitad.com` разрешён только при выполнении внешнего бизнес-условия.
 * Если redirect_uri указывает на `lite.admitad.com`, но условие не выполнено — выполняется fallback redirect.
 *
 * @param {string | null} redirectUri — значение query-параметра `redirect_uri`
 * @param {boolean} allowLiteAdmitadRedirect — разрешить redirect на `lite.admitad.com`
 * @returns {string} абсолютный URL с учётом cmpimport
 */
export function resolveExitUrlFromRedirectUriParamWithLiteAdmitadGate(
  redirectUri: string | null,
  allowLiteAdmitadRedirect: boolean,
): string {
  if (typeof redirectUri === 'string' && redirectUri !== '' && isLiteAdmitadRedirectUri(redirectUri)) {
    if (!allowLiteAdmitadRedirect) {
      return resolveExitUrlFromRedirectUriParam(null);
    }
    // `lite.admitad.com` — строгое бизнес-исключение: разрешаем только этот хост и стандартные порты,
    // даже если он не добавлен в общий allowlist.
    return applyCmpimportToRedirectUrl(redirectUri);
  }
  return resolveExitUrlFromRedirectUriParam(redirectUri);
}

/**
 * Собирает целевой URL после survey gate: `redirect_uri` из query переданного `pageUrl`, иначе fallback.
 *
 * @param {string} pageUrl — URL страницы с параметром `redirect_uri` в query
 * @returns {string} абсолютный URL с учётом cmpimport
 */
export function prepareBusinessUnitRedirectFromPageUrl(pageUrl: string): string {
  return resolveExitUrlFromRedirectUriParam(readRedirectUriFromSurveyPageUrl(pageUrl));
}

/**
 * Редирект после survey по **только** query текущего окна: берётся `redirect_uri`, без использования полного `href` как цели.
 *
 * @returns {string}
 */
export function prepareBusinessUnitRedirectFromWindow(): string {
  if (typeof window === 'undefined' || typeof window.location.search !== 'string') {
    throw new TypeError('prepareBusinessUnitRedirectFromWindow: window.location.search is not available');
  }
  return resolveExitUrlFromRedirectUriParam(new URLSearchParams(window.location.search).get('redirect_uri'));
}
